import '../../../styles/restScreens.css';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { supabaseClient } from '../../../config/supabase';
import Button3D from '../../ui/Button3D';
import Modal3D from '../../ui/Modal3D';
import ActionPillMenu from '../../ui/ActionPillMenu';
import HoverHelp from '../../shared/HoverHelp';
import useModuleAccess from '../../../hooks/useModuleAccess';
import { GUESTS_SCREEN_LABEL, RSVP_STATUSES, rsvpLabel } from '../eventLabels';
import { DEFAULT_EVENT_SETTINGS, rsvpChoices } from '../eventSettings';
import { loadEventSettingsOrDefaults } from '../eventSettingsApi';
import { resolveEventWords } from '../eventWords';
import {
    filterGuests, friendlyGuestError, guestCounts, guestName, guestPhoneLabel, orderGuests, plusOnesOf,
} from './guestConstants';
import {
    createGroup, createGuest, createPlusOne, deleteGroup, deleteGuest, importGuests, loadEventForGuests, loadGroups, loadGuests,
    renameGroup, setGuestRsvp, updateGuest,
} from './guestsApi';
import { GUEST_INPUT_CLASS, ROW_GRID, ROW_GRID_CHECKIN, RSVP_DOT_COLORS, RSVP_TRIGGER_CLASS, guestPill } from './guestStyles';
import GuestFormPopup from './GuestFormPopup';
import GuestGroupsPopup from './GuestGroupsPopup';
import GuestImportPopup from './GuestImportPopup';
import HotelBlockPopup from '../hotel/HotelBlockPopup';
import HotelTravelPanel from '../hotel/HotelTravelPanel';
import { deleteHotelBlock, friendlyHotelError, loadHotelBlock, saveHotelBlock } from '../hotel/hotelApi';
import { shuttleCount } from '../hotel/hotelBlock';
import GuestTransportPanel from '../transport/GuestTransportPanel';
import { attendanceCsv, attendanceFileName, attendanceSummary, filterByCheckin, formatClock } from '../checkin/checkin';
import { checkInGuest, friendlyCheckinError, loadAttendance, loadAttendanceReport, loadCheckins, undoCheckIn } from '../checkin/checkinApi';
import { GuestPassPopup, PassSheetPopup } from '../checkin/PassPopups';
import { downloadCsv } from '../eventsBoard';

const helpFor = (words) => ({
    title: 'Guests & RSVP',
    body: `Everyone invited to this ${words.eventLower}, with their RSVP. Add guests one at a time or import a list. Guests are not CRM contacts, and nothing is sent from this screen.`,
});

// A coloured dot and the word. Colour is never the only signal.
function RsvpWord({ status }) {
    return (
        <>
            <span aria-hidden="true" data-rsvp-dot={status} className="inline-block h-2.5 w-2.5 shrink-0 rounded-full" style={{ background: RSVP_DOT_COLORS[status] || RSVP_DOT_COLORS.pending }} />
            <span>{rsvpLabel(status)}</span>
        </>
    );
}

function CountTile({ label, value, note, testId }) {
    return (
        <div className="ia-d2-card min-w-0" data-testid={testId}>
            <div className="text-xs font-bold uppercase tracking-wide">{label}</div>
            <div className="text-2xl font-bold leading-tight">{value}</div>
            {note && <div className="text-sm" data-testid={testId ? `${testId}-note` : undefined}>{note}</div>}
        </div>
    );
}

// Module 19 (BL-380) G2 - "Guests & RSVP" for one event: counts, search and filters, the list (cards on a phone), add / edit / plus-ones,
// groups, and the CSV import. Staff set RSVPs by hand here; nothing is sent to anyone.
export default function GuestsView({ engagementId, userId, isUser = false, onBack, onOpenDoor }) {
    // G8: Events has its own permissions area; planners never need Dispatch rights.
    const { allowed: eventsEditAllowed } = useModuleAccess(isUser ? 'events' : null, 'edit');
    const canEdit = !isUser || eventsEditAllowed;

    const [settings, setSettings] = useState(DEFAULT_EVENT_SETTINGS);
    const [event, setEvent] = useState(null);
    // Q-046: the company's word for an event (breadcrumb, help, remove dialog, guest form)
    const words = useMemo(() => resolveEventWords(settings), [settings]);
    // A new or imported guest starts from this event's own plus-one setting (migration 289), not a company-wide default.
    const guestSettings = useMemo(() => (event?.plus_ones_enabled === undefined ? settings
        : { ...settings, allow_plus_ones: event.plus_ones_enabled === true, default_plus_ones_allowed: Number(event.plus_ones_max) || 0 }), [settings, event]);
    const [guests, setGuests] = useState([]);
    const [groups, setGroups] = useState([]);
    const [loading, setLoading] = useState(true);
    const [loadError, setLoadError] = useState('');
    const [actionError, setActionError] = useState('');
    const [search, setSearch] = useState('');
    const [rsvp, setRsvp] = useState('all');
    const [group, setGroup] = useState('all');

    const [form, setForm] = useState(null);           // { mode: 'add'|'edit'|'plus-one', guest, host }
    const [saving, setSaving] = useState(false);
    const [formError, setFormError] = useState('');
    const [importing, setImporting] = useState(false);
    const [groupsOpen, setGroupsOpen] = useState(false);
    const [groupsBusy, setGroupsBusy] = useState(false);
    const [groupsError, setGroupsError] = useState('');
    const [removing, setRemoving] = useState(null);
    // G6: the hotel block and the shuttle filter
    const [hotel, setHotel] = useState(null);
    const [hotelOpen, setHotelOpen] = useState(false);
    const [hotelSaving, setHotelSaving] = useState(false);
    const [hotelError, setHotelError] = useState('');
    const [shuttleOnly, setShuttleOnly] = useState(false);
    // G5: who is checked in (null = check-in not available yet), the event's attendance numbers, and the pass / undo popups
    const [checkins, setCheckins] = useState(null);
    const [attendance, setAttendance] = useState(null);
    const [checkinFilter, setCheckinFilter] = useState('all');
    const [passGuest, setPassGuest] = useState(null);
    const [sheetOpen, setSheetOpen] = useState(false);
    const [undoing, setUndoing] = useState(null);
    const [checkinBusy, setCheckinBusy] = useState('');

    const refresh = useCallback(async () => {
        try {
            const [nextGuests, nextGroups] = await Promise.all([loadGuests(supabaseClient, engagementId), loadGroups(supabaseClient, engagementId)]);
            setGuests(nextGuests);
            setGroups(nextGroups);
            setLoadError('');
        } catch (error) {
            setLoadError(friendlyGuestError(error));
        }
        // G5: check-in state is read separately, so the guest list still works if it cannot be read
        try {
            const [nextCheckins, nextAttendance] = await Promise.all([loadCheckins(supabaseClient, engagementId), loadAttendance(supabaseClient, engagementId)]);
            setCheckins(nextCheckins);
            setAttendance(nextAttendance);
        } catch {
            setCheckins(null);
            setAttendance(null);
        }
        setLoading(false);
    }, [engagementId]);

    useEffect(() => {
        let alive = true;
        loadEventForGuests(supabaseClient, engagementId).then((value) => { if (alive) setEvent(value); }).catch(() => {});
        loadEventSettingsOrDefaults(supabaseClient).then((value) => { if (alive) setSettings(value); });
        loadHotelBlock(supabaseClient, engagementId).then((value) => { if (alive) setHotel(value); }).catch(() => {});
        refresh();
        return () => { alive = false; };
    }, [engagementId, refresh]);

    // "Maybe" turned off hides it from menus, forms, filters and import; a Maybe already on this list keeps its status and stays visible so the counts add up.
    const showMaybe = settings.allow_maybe || guests.some((guest) => guest.rsvp_status === 'maybe');
    const groupsById = useMemo(() => Object.fromEntries(groups.map((item) => [item.id, item])), [groups]);
    const counts = useMemo(() => guestCounts(guests, event?.expected_guest_count), [guests, event]);
    const showShuttle = settings.shuttle_enabled !== false;
    const showHotel = settings.hotel_block_enabled !== false;
    // G5: check-in shows when it is switched on and the database has it; QR passes and the door scanner need their own switch too
    const showCheckin = settings.checkin_enabled !== false && checkins !== null;
    const showQr = showCheckin && settings.checkin_qr_enabled !== false;
    const eventCanceled = event?.engagement?.status === 'canceled';
    const canCheckIn = showCheckin && canEdit && !eventCanceled;
    const attendanceView = useMemo(() => (showCheckin ? attendanceSummary(attendance) : null), [showCheckin, attendance]);
    const visible = useMemo(() => orderGuests(filterByCheckin(
        filterGuests(guests, { search, rsvp, group, groupsById }).filter((guest) => !(showShuttle && shuttleOnly) || guest.needs_shuttle === true),
        showCheckin ? checkins : null, checkinFilter,
    )), [guests, search, rsvp, group, groupsById, showShuttle, shuttleOnly, showCheckin, checkins, checkinFilter]);
    const shuttle = useMemo(() => shuttleCount(guests), [guests]);
    const hostName = (guest) => guestName(guests.find((item) => item.id === guest.plus_one_of));
    const groupOf = (guest) => groupsById[guest.group_id]?.name || (guest.plus_one_of ? groupsById[guests.find((item) => item.id === guest.plus_one_of)?.group_id]?.name : '') || '';
    const roomLeft = (guest) => !guest.plus_one_of && plusOnesOf(guests, guest.id).length < (guest.plus_ones_allowed || 0);

    const attempt = async (work, setError = setActionError) => {
        setError('');
        try { await work(); await refresh(); return true; } catch (error) { setError(friendlyGuestError(error)); return false; }
    };

    const saveGuest = async (values) => {
        setSaving(true); setFormError('');
        const ok = await attempt(async () => {
            if (form.mode === 'plus-one') await createPlusOne(supabaseClient, form.host, values);
            else if (form.guest) await updateGuest(supabaseClient, form.guest.id, values, userId);
            else await createGuest(supabaseClient, engagementId, values);
        }, setFormError);
        setSaving(false);
        if (ok) setForm(null);
    };

    const changeRsvp = (guest, status) => attempt(() => setGuestRsvp(supabaseClient, guest.id, status, userId));
    const confirmRemove = async () => { const guest = removing; setRemoving(null); await attempt(() => deleteGuest(supabaseClient, guest.id)); };

    const groupAction = async (work) => { setGroupsBusy(true); await attempt(work, setGroupsError); setGroupsBusy(false); };

    const saveHotel = async (values) => {
        setHotelSaving(true); setHotelError('');
        try {
            setHotel(await saveHotelBlock(supabaseClient, engagementId, hotel, values, userId));
            setHotelOpen(false);
        } catch (error) { setHotelError(friendlyHotelError(error)); }
        setHotelSaving(false);
    };
    const removeHotel = async () => {
        setHotelSaving(true); setHotelError('');
        try { await deleteHotelBlock(supabaseClient, hotel.id); setHotel(null); setHotelOpen(false); } catch (error) { setHotelError(friendlyHotelError(error)); }
        setHotelSaving(false);
    };

    const checkIn = async (guest) => {
        setCheckinBusy(guest.id); setActionError('');
        try { await checkInGuest(supabaseClient, engagementId, guest.id); await refresh(); } catch (error) { setActionError(friendlyCheckinError(error)); }
        setCheckinBusy('');
    };
    const confirmUndo = async () => {
        const guest = undoing; setUndoing(null); setCheckinBusy(guest.id); setActionError('');
        try { await undoCheckIn(supabaseClient, engagementId, guest.id); await refresh(); } catch (error) { setActionError(friendlyCheckinError(error)); }
        setCheckinBusy('');
    };
    const downloadReport = async () => {
        setActionError('');
        try {
            const report = await loadAttendanceReport(supabaseClient, engagementId);
            downloadCsv(attendanceFileName(report), attendanceCsv(report));
        } catch (error) { setActionError(friendlyCheckinError(error)); }
    };

    const runImport = async (rows) => {
        const response = await importGuests(supabaseClient, engagementId, rows);
        return response;
    };

    const rowActions = (guest) => {
        const options = [{ key: 'edit', label: 'Edit guest', onSelect: () => { setFormError(''); setForm({ mode: 'edit', guest }); } }];
        if (!guest.plus_one_of) {
            options.push({
                key: 'plus-one', label: 'Add plus-one', disabled: !roomLeft(guest),
                title: roomLeft(guest) ? undefined : guest.plus_ones_allowed ? 'All plus-ones for this guest are added' : 'Set “Plus-ones allowed” on this guest first',
                onSelect: () => { setFormError(''); setForm({ mode: 'plus-one', host: guest }); },
            });
        }
        if (showQr && !eventCanceled) options.push({ key: 'pass', label: 'Show QR pass', onSelect: () => setPassGuest(guest) });
        if (canCheckIn && checkins.has(guest.id)) options.push({ key: 'undo-checkin', label: 'Undo check-in', onSelect: () => setUndoing(guest) });
        options.push({ key: 'delete', label: 'Remove guest', onSelect: () => setRemoving(guest) });
        return options;
    };

    const rsvpControl = (guest) => (canEdit ? (
        <ActionPillMenu
            menuLabel={`RSVP for ${guestName(guest)}`}
            placement="bottom"
            align="start"
            size="compact"
            options={rsvpChoices(RSVP_STATUSES, settings, guest.rsvp_status).map((status) => ({ key: status, label: rsvpLabel(status), active: guest.rsvp_status === status, onSelect: () => changeRsvp(guest, status) }))}
            triggerClassName={RSVP_TRIGGER_CLASS}
            triggerProps={{ 'aria-label': `RSVP: ${rsvpLabel(guest.rsvp_status)}. Change RSVP for ${guestName(guest)}` }}
        >
            <RsvpWord status={guest.rsvp_status} /><span aria-hidden="true">▾</span>
        </ActionPillMenu>
    ) : <span className="inline-flex items-center gap-2 text-sm font-bold"><RsvpWord status={guest.rsvp_status} /></span>);

    const label = (text) => <span className="lg:hidden block text-xs font-bold uppercase tracking-wide">{text}</span>;

    // G5: the Check-in cell. In = a tick, the time and how; not in = a "Check in" button for staff who may.
    const checkinCell = (guest) => {
        const row = checkins?.get(guest.id);
        if (row) {
            return (
                <span className="inline-flex flex-wrap items-center gap-x-2 text-sm" data-testid="guest-checked-in">
                    <span className="ia-checkin-in font-bold">✓ In · {formatClock(row.checked_in_at)}</span>
                    <span>{row.method === 'qr' ? 'QR pass' : 'From the list'}</span>
                </span>
            );
        }
        if (!canCheckIn) return <span className="text-sm">Not checked in</span>;
        return (
            <Button3D variant="pill" onClick={() => checkIn(guest)} disabled={checkinBusy === guest.id} data-testid="guest-checkin" aria-label={`Check in ${guestName(guest)}`}>
                {checkinBusy === guest.id ? 'Checking in…' : 'Check in'}
            </Button3D>
        );
    };
    const headings = showCheckin ? ['Guest', 'Contact', 'Group', 'RSVP', 'Plus-ones', 'Check-in', ''] : ['Guest', 'Contact', 'Group', 'RSVP', 'Plus-ones', ''];
    const rowGrid = showCheckin ? ROW_GRID_CHECKIN : ROW_GRID;

    return (
        <section className="ia-d2 ia-events rounded-2xl p-3 sm:p-5 space-y-4" data-m6-layout="events" data-m6-component="EventGuestsView" aria-label={GUESTS_SCREEN_LABEL}>
            <div className="flex flex-wrap items-start justify-between gap-3">
                <div className="min-w-0">
                    <nav aria-label="Breadcrumb" data-testid="guests-breadcrumb">
                        <ol className="flex flex-wrap items-center gap-2">
                            <li className="flex items-center gap-2"><Button3D variant="pill" onClick={onBack} data-testid="guests-crumb-events">{words.events}</Button3D><span aria-hidden="true">›</span></li>
                            <li className="flex min-w-0 items-center gap-2"><span className="min-w-0 break-words font-semibold" data-testid="guests-crumb-event">{event?.event_name || `This ${words.eventLower}`}</span><span aria-hidden="true">›</span></li>
                        </ol>
                    </nav>
                    <div className="mt-2 flex items-center gap-2">
                        <h2 className="text-2xl font-bold">{GUESTS_SCREEN_LABEL}</h2>
                        <HoverHelp helpId="events.guests" content={helpFor(words)} userId={userId} />
                    </div>
                </div>
                {(canEdit || showCheckin) && (
                    <div className="flex flex-wrap items-center gap-2">
                        {canEdit && <Button3D variant="go" onClick={() => { setFormError(''); setForm({ mode: 'add' }); }}>Add guest</Button3D>}
                        {canEdit && <Button3D onClick={() => setImporting(true)}>Import list</Button3D>}
                        {canEdit && <Button3D onClick={() => { setGroupsError(''); setGroupsOpen(true); }}>Groups</Button3D>}
                        {showQr && canCheckIn && onOpenDoor && <Button3D variant="go" onClick={onOpenDoor} data-testid="open-door">Door check-in</Button3D>}
                        {showQr && canEdit && !eventCanceled && <Button3D onClick={() => setSheetOpen(true)} data-testid="open-pass-sheet">QR passes</Button3D>}
                        {showCheckin && <Button3D onClick={downloadReport} data-testid="attendance-report">Attendance report</Button3D>}
                    </div>
                )}
            </div>

            <div className="space-y-2" data-testid="guest-counts">
                <div className="grid grid-cols-2 gap-2 sm:grid-cols-4">
                    <CountTile testId="count-invited" label="Invited" value={counts.invited} />
                    <CountTile testId="count-yes" label="Yes" value={counts.accepted} />
                    <CountTile testId="count-no" label="No" value={counts.declined} />
                    {showMaybe ? (
                        <CountTile testId="count-maybe-noreply" label="Maybe / No reply" value={counts.maybe + counts.pending} note={`${counts.maybe} maybe · ${counts.pending} no reply`} />
                    ) : (
                        <CountTile testId="count-maybe-noreply" label="No reply" value={counts.pending} />
                    )}
                </div>
                <div className={`grid grid-cols-1 gap-2 ${attendanceView ? 'sm:grid-cols-3' : 'sm:grid-cols-2'}`}>
                    <CountTile
                        testId="count-confirmed"
                        label="Confirmed heads"
                        value={counts.confirmedHeads}
                        note={counts.overBy ? `${counts.overBy} over your expected count of ${counts.expected}` : 'Guests who said Yes and their plus-ones'}
                    />
                    <CountTile
                        testId="count-projected"
                        label="Projected heads"
                        value={counts.expected !== null ? `${counts.projectedHeads} of ${counts.expected}` : counts.projectedHeads}
                        note={counts.expected === null ? 'No expected count set' : 'expected'}
                    />
                    {attendanceView && (
                        <CountTile testId="count-checked-in" label="Checked in" value={attendanceView.checkedIn} note={attendanceView.note} />
                    )}
                </div>
            </div>
            <HotelTravelPanel block={hotel} showHotel={showHotel} showShuttle={showShuttle} shuttle={shuttle} canEdit={canEdit} onEdit={() => { setHotelError(''); setHotelOpen(true); }} />
            {/* G9a: the shuttle needs become ordinary Dispatch trips in the same company account (checked in the database) */}
            <GuestTransportPanel client={supabaseClient} engagementId={engagementId} showShuttle={showShuttle} shuttle={loading ? null : shuttle} words={words} />

            <p className="text-sm" data-testid="heads-note">
                Confirmed heads are guests who said Yes plus their plus-ones, the number to give the caterer. Projected heads are everyone who has not said No, plus-ones included{counts.plusOnes > 0 ? ` (${counts.plusOnes} ${counts.plusOnes === 1 ? 'plus-one' : 'plus-ones'} on this list)` : ''}.
            </p>

            <div className="space-y-3" data-testid="guest-toolbar">
                <input type="search" aria-label="Search guests" placeholder="Search by name, email or phone…" value={search} onChange={(e) => setSearch(e.target.value)} className={GUEST_INPUT_CLASS} />
                <div className="flex flex-wrap items-center gap-2" role="group" aria-label="Filter by RSVP">
                    {['all', ...rsvpChoices(RSVP_STATUSES, { ...settings, allow_maybe: showMaybe })].map((status) => (
                        <button key={status} type="button" aria-pressed={rsvp === status} onClick={() => setRsvp(status)} className={guestPill(rsvp === status)}>{status === 'all' ? 'All' : rsvpLabel(status)}</button>
                    ))}
                </div>
                {showShuttle && (
                    <div className="flex flex-wrap items-center gap-2" role="group" aria-label="Filter by travel">
                        <button type="button" aria-pressed={shuttleOnly} onClick={() => setShuttleOnly((value) => !value)} className={guestPill(shuttleOnly)} data-testid="filter-shuttle">Needs shuttle</button>
                    </div>
                )}
                {showCheckin && (
                    <div className="flex flex-wrap items-center gap-2" role="group" aria-label="Filter by check-in">
                        {[['all', 'Everyone'], ['in', 'Checked in'], ['out', 'Not checked in']].map(([key, text]) => (
                            <button key={key} type="button" aria-pressed={checkinFilter === key} onClick={() => setCheckinFilter(key)} className={guestPill(checkinFilter === key)} data-testid={`filter-checkin-${key}`}>{text}</button>
                        ))}
                    </div>
                )}
                {groups.length > 0 && (
                    <div className="flex flex-wrap items-center gap-2" role="group" aria-label="Filter by group">
                        <button type="button" aria-pressed={group === 'all'} onClick={() => setGroup('all')} className={guestPill(group === 'all')}>All groups</button>
                        {groups.map((item) => (
                            <button key={item.id} type="button" aria-pressed={group === item.id} onClick={() => setGroup(item.id)} className={guestPill(group === item.id)}>{item.name}</button>
                        ))}
                        <button type="button" aria-pressed={group === 'none'} onClick={() => setGroup('none')} className={guestPill(group === 'none')}>No group</button>
                    </div>
                )}
            </div>

            {(loadError || actionError) && <p role="alert" className="ia-resv-error">{loadError || actionError}</p>}

            {loading ? (
                <p className="py-6 text-center">Loading…</p>
            ) : visible.length === 0 ? (
                <div className="ia-d2-card text-center" data-testid="guests-empty">
                    <div className="font-semibold">{guests.length === 0 ? 'No guests yet' : 'Nothing matches those filters'}</div>
                    <div className="text-sm">{guests.length === 0 ? 'Add your first guest, or import a list.' : 'Try another RSVP, group, shuttle, check-in or search filter.'}</div>
                </div>
            ) : (
                // No overflow clip on this card: the RSVP and Edit flyover menus of the last rows open below it and must stay whole (DC-1).
                <div role="table" aria-label="Guest list" className="ia-d2-card !p-0" data-testid="guest-list">
                    <div role="row" className={`hidden rounded-t-[15px] border-b border-[#26365A] bg-[#0A1120] px-3 py-2 text-xs font-bold uppercase tracking-wide lg:grid lg:gap-3 ${rowGrid}`}>
                        {headings.map((heading, index) => <div key={heading || `a${index}`} role="columnheader">{heading}</div>)}
                    </div>
                    {visible.map((guest) => {
                        const taken = plusOnesOf(guests, guest.id).length;
                        return (
                            <div key={guest.id} role="row" data-testid="guest-row" data-guest-id={guest.id} className={`grid grid-cols-1 gap-2 border-b border-[#26365A] px-3 py-3 last:border-b-0 lg:items-center lg:gap-3 ${rowGrid}`}>
                                <div role="cell" className={`min-w-0 ${guest.plus_one_of ? 'pl-5' : ''}`}>
                                    <div className="break-words font-semibold">{guestName(guest)}</div>
                                    {guest.plus_one_of && <div className="text-sm">Plus-one of {hostName(guest)}</div>}
                                    {guest.notes && <div className="break-words text-sm">{guest.notes}</div>}
                                    {showShuttle && guest.needs_shuttle && <div className="break-words text-sm" data-testid="guest-shuttle">🚐 Needs the shuttle{guest.arrival_info ? ` · ${guest.arrival_info}` : ''}</div>}
                                </div>
                                <div role="cell" className="min-w-0 text-sm">
                                    {label('Contact')}
                                    {guest.email && <div className="break-all">{guest.email}</div>}
                                    {guest.phone && <div data-phone={guest.phone}>{guestPhoneLabel(guest)}</div>}
                                    {!guest.email && !guest.phone && <div>No contact details</div>}
                                </div>
                                <div role="cell" className="min-w-0 text-sm">{label('Group')}{groupOf(guest) || 'No group'}</div>
                                <div role="cell">{label('RSVP')}{rsvpControl(guest)}</div>
                                <div role="cell" className="min-w-0 text-sm">
                                    {label('Plus-ones')}
                                    {guest.plus_one_of ? '—' : (
                                        <>
                                            <div>{guest.plus_ones_allowed ? `${taken} of ${guest.plus_ones_allowed} added` : 'None allowed'}</div>
                                            {canEdit && roomLeft(guest) && <Button3D variant="pill" className="mt-1" onClick={() => { setFormError(''); setForm({ mode: 'plus-one', host: guest }); }}>Add plus-one</Button3D>}
                                        </>
                                    )}
                                </div>
                                {showCheckin && <div role="cell" className="min-w-0">{label('Check-in')}{checkinCell(guest)}</div>}
                                <div role="cell" className="lg:justify-self-end">
                                    {canEdit ? (
                                        <ActionPillMenu
                                            menuLabel={`Actions for ${guestName(guest)}`}
                                            placement="bottom"
                                            align="end"
                                            size="compact"
                                            options={rowActions(guest)}
                                            onTriggerClick={() => { setFormError(''); setForm({ mode: 'edit', guest }); }}
                                            triggerClassName={`${guestPill(false)} inline-flex items-center gap-1`}
                                            triggerProps={{ 'aria-label': `Edit ${guestName(guest)}` }}
                                        >
                                            <span>Edit</span><span aria-hidden="true">▾</span>
                                        </ActionPillMenu>
                                    ) : null}
                                </div>
                            </div>
                        );
                    })}
                </div>
            )}

            {form && (
                <GuestFormPopup
                    words={words}
                    mode={form.mode}
                    guest={form.guest}
                    host={form.host}
                    groups={groups}
                    guests={guests}
                    settings={guestSettings}
                    saving={saving}
                    error={formError}
                    onSave={saveGuest}
                    onClose={() => setForm(null)}
                />
            )}
            {hotelOpen && <HotelBlockPopup block={hotel} event={event} words={words} saving={hotelSaving} error={hotelError} onSave={saveHotel} onRemove={removeHotel} onClose={() => setHotelOpen(false)} />}
            {importing && <GuestImportPopup engagementId={engagementId} existingGuests={guests} settings={guestSettings} onImport={runImport} onDone={refresh} onClose={() => setImporting(false)} />}
            {groupsOpen && (
                <GuestGroupsPopup
                    groups={groups}
                    guests={guests}
                    busy={groupsBusy}
                    error={groupsError}
                    onCreate={(name) => groupAction(() => createGroup(supabaseClient, engagementId, name, groups.length + 1))}
                    onRename={(id, name) => groupAction(() => renameGroup(supabaseClient, id, name, userId))}
                    onDelete={(id) => groupAction(() => deleteGroup(supabaseClient, id))}
                    onClose={() => setGroupsOpen(false)}
                />
            )}
            {passGuest && <GuestPassPopup client={supabaseClient} engagementId={engagementId} guest={passGuest} canReplace={canEdit} onClose={() => setPassGuest(null)} />}
            {sheetOpen && <PassSheetPopup client={supabaseClient} engagementId={engagementId} words={words} onClose={() => setSheetOpen(false)} />}
            {undoing && (
                <Modal3D
                    title="Undo this check-in?"
                    subtitle={`${guestName(undoing)} will show as not checked in.`}
                    role="alertdialog"
                    size="small"
                    stripe="gold"
                    testId="checkin-undo-popup"
                    onClose={() => setUndoing(null)}
                    data-m6-layout="modals"
                    data-m6-component="EventCheckinUndoPopup"
                    actions={(<><Button3D onClick={() => setUndoing(null)}>Keep check-in</Button3D><Button3D variant="go" onClick={confirmUndo}>Undo check-in</Button3D></>)}
                >
                    <p className="text-sm">The history keeps a record of the check-in and of this undo, with your name. Their pass still works if they come back.</p>
                </Modal3D>
            )}
            {removing && (
                <Modal3D
                    title="Remove this guest?"
                    subtitle={`${guestName(removing)}${plusOnesOf(guests, removing.id).length ? ' and their plus-one(s) will be removed from the list.' : ' will be removed from the list.'}`}
                    role="alertdialog"
                    size="small"
                    stripe="gold"
                    testId="guest-remove-popup"
                    onClose={() => setRemoving(null)}
                    data-m6-layout="modals"
                    data-m6-component="EventGuestRemovePopup"
                    actions={(<><Button3D onClick={() => setRemoving(null)}>Keep guest</Button3D><Button3D variant="go" onClick={confirmRemove}>Remove guest</Button3D></>)}
                >
                    <p className="text-sm">This only changes this {words.eventLower}’s guest list. Nobody is notified.</p>
                </Modal3D>
            )}
        </section>
    );
}
